import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/lib/supabase';

export interface DerivedStage {
  id: string;
  stage_name: string;
  status: string;
  completion_percent: number;
  start_date: string | null;
  end_date: string | null;
  planned_start_date: string | null;
  planned_end_date: string | null;
  actual_start_date: string | null;
  actual_end_date: string | null;
  sort_order: number;
  item_count: number;
  completed_items: number;
  description: string | null;
}

export interface UseProjectStagesResult {
  stages: DerivedStage[];
  currentStage: DerivedStage | null;
  projectStageStatus: 'completed' | 'in_progress' | 'not_started';
  stageByDate: (dateKey: string) => DerivedStage | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

function isScheduleItemComplete(item: {
  is_complete: boolean;
  progress: number;
  actual_end_date: string | null;
}): boolean {
  if (item.is_complete) return true;
  if (item.progress >= 100) return true;
  if (item.actual_end_date != null && item.actual_end_date !== '') return true;
  return false;
}

export function useProjectStages(projectId: string | undefined): UseProjectStagesResult {
  type ScheduleItemRow = {
    id: string;
    title: string;
    main_stage: string | null;
    phase: string | null;
    start_date: string | null;
    end_date: string | null;
    actual_start_date: string | null;
    actual_end_date: string | null;
    progress: number;
    is_complete: boolean;
    is_included: boolean;
    sort_order: number;
  };

  const [scheduleItems, setScheduleItems] = useState<ScheduleItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const hasLoadedOnce = useRef(false);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  const load = useCallback(async () => {
    if (!projectId) {
      setScheduleItems([]);
      setLoading(false);
      return;
    }
    if (!hasLoadedOnce.current) {
      setLoading(true);
    }
    setError(null);

    try {
      const { data, error: err } = await supabase
        .from('jphph_project_schedule_items')
        .select('id, title, main_stage, phase, start_date, end_date, actual_start_date, actual_end_date, progress, is_complete, is_included, sort_order')
        .eq('project_id', projectId)
        .eq('is_included', true)
        .eq('is_deleted', false)
        .order('sort_order', { ascending: true });

      if (err) throw err;
      setScheduleItems(data || []);
    } catch (e) {
      setError(String(e));
    } finally {
      hasLoadedOnce.current = true;
      setLoading(false);
    }
  }, [projectId, tick]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!projectId) return;
    const channel = supabase
      .channel(`project-stages-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'jphph_project_schedule_items',
          filter: `project_id=eq.${projectId}`,
        },
        () => {
          setTimeout(() => refresh(), 200);
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [projectId, refresh]);

  const stages = useMemo((): DerivedStage[] => {
    if (scheduleItems.length === 0) return [];

    const groups = new Map<string, typeof scheduleItems>();
    for (const item of scheduleItems) {
      const stageName = item.main_stage || item.phase || 'General';
      if (!groups.has(stageName)) groups.set(stageName, []);
      groups.get(stageName)!.push(item);
    }

    const derived: DerivedStage[] = [];
    for (const [stageName, items] of groups) {
      const total = items.length;
      const completed = items.filter(isScheduleItemComplete).length;
      const completionPercent = total > 0 ? Math.round((completed / total) * 100) : 0;

      let status = 'not_started';
      if (completionPercent >= 100) status = 'completed';
      else if (completionPercent > 0) status = 'in_progress';

      const startDates = items.map((i) => i.start_date).filter((d): d is string => !!d);
      const endDates = items.map((i) => i.end_date).filter((d): d is string => !!d);
      const actualStartDates = items.map((i) => i.actual_start_date).filter((d): d is string => !!d);
      const actualEndDates = items.map((i) => i.actual_end_date).filter((d): d is string => !!d);

      const startDate = startDates.length > 0 ? startDates.sort()[0] : null;
      const endDate = endDates.length > 0 ? endDates.sort((a, b) => a.localeCompare(b))[endDates.length - 1] : null;
      const actualStartDate = actualStartDates.length > 0 ? actualStartDates.sort()[0] : null;
      const actualEndDate = actualEndDates.length > 0 ? actualEndDates.sort((a, b) => a.localeCompare(b))[actualEndDates.length - 1] : null;

      const minSortOrder = Math.min(...items.map((i) => i.sort_order));

      derived.push({
        id: `${projectId}::${stageName}`,
        stage_name: stageName,
        status,
        completion_percent: completionPercent,
        start_date: startDate,
        end_date: endDate,
        planned_start_date: startDate,
        planned_end_date: endDate,
        actual_start_date: actualStartDate,
        actual_end_date: actualEndDate,
        sort_order: minSortOrder,
        item_count: total,
        completed_items: completed,
        description: null,
      });
    }

    // Sort by actual date order (start_date), not hardcoded stage names
    derived.sort((a, b) => {
      const aStart = a.start_date || '9999-12-31';
      const bStart = b.start_date || '9999-12-31';
      if (aStart !== bStart) return aStart.localeCompare(bStart);
      return a.sort_order - b.sort_order;
    });

    return derived;
  }, [scheduleItems, projectId]);

  const currentStage = useMemo(() => {
    if (stages.length === 0) return null;
    const active = stages.find((s) => s.status !== 'completed');
    if (active) return active;
    return stages[stages.length - 1];
  }, [stages]);

  const projectStageStatus = useMemo(() => {
    if (stages.length === 0) return 'not_started';
    const allComplete = stages.every((s) => s.status === 'completed');
    if (allComplete) return 'completed';
    const anyInProgress = stages.some((s) => s.status === 'in_progress');
    if (anyInProgress) return 'in_progress';
    return 'not_started';
  }, [stages]);

  const stageByDate = useCallback((dateKey: string): DerivedStage | null => {
    for (const stage of stages) {
      if (!stage.start_date || !stage.end_date) continue;
      if (dateKey >= stage.start_date && dateKey <= stage.end_date) {
        return stage;
      }
    }
    return null;
  }, [stages]);

  return { stages, currentStage, projectStageStatus, stageByDate, loading, error, refresh };
}